import { trackUserEvent } from '@vend/utilities'
import { useEffect, useMemo } from 'react'
import { WEB_REGISTER } from '../../web-register.constant'

type TrackEndEventFunction = (extraData?: any) => void

/**
 * Hook that will fire an UBE upon initial render indicating the start of an event.
 *
 * @param message - The message of the UBEs to be fired.
 *                  The start event will look like "{message}: start".
 *                  The end event will look like "{message}: end"
 * @param registerId - The id of the register the payment is being made on.
 * @param amount - The amount of the payment.
 * @param paymentTypeId - The id of the payment type the payment is being used on.
 * @param extraData - Any extra data you want to attach to the events.
 *                    This will be attached to both the start and end events.
 *
 * @returns A function that will track the end of an event.
 *          When it is called it will fire an UBE indicating the end of the event, with the time elapsed since the start event.
 */
export const useTrackPaymentDuration = (
  message: string,
  registerId: string,
  amount: string | number,
  paymentTypeId: number,
  extraData: any
): TrackEndEventFunction => {
  const startTime = useMemo(() => Date.now(), [])

  // track start
  useEffect(() => {
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, `${message}: start`, {
      register: registerId,
      amount,
      pt: paymentTypeId,
      ...extraData,
    })
    // we only want this effect to run once on first render, so we omit dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * This function is returned from the hook.
   * When called it will emit an "end" event with the time elapsed since the start event.
   *
   * @param endExtraData - Any extra data you want to attach to the end event.
   */
  const trackEnd: TrackEndEventFunction = (endExtraData?: any) => {
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, `${message}: end`, {
      register: registerId,
      amount,
      pt: paymentTypeId,
      // Duration in seconds, when ending the flow
      duration: (Date.now() - startTime) / 1000,
      ...extraData,
      ...endExtraData,
    })
  }

  return trackEnd
}
